// Tranche CA closure: resident's /me/credentials surface.
//
// Lists the credentials currently bound to guest passes the caller has
// sponsored. Today's schema doesn't model personal resident credentials
// directly — Credential rows hang off Employee. So the page is narrowly
// labeled "Active credentials for your guests" and intentionally does
// not promise a personal credential list.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import {
  credentialStatusLabel,
  credentialTypeLabel,
  syncStatusLabel,
} from '../lib/humanLabels.js';
import { useIsCompact } from '../lib/useIsCompact.js';

interface CredentialLite {
  id: string;
  type: 'nfc_card' | 'mobile_key' | 'pin_code';
  status: 'pending' | 'active' | 'revoked';
  label: string | null;
  unifi_credential_id: string | null;
  sync_status: 'pending' | 'syncing' | 'synced' | 'failed';
  last_sync_error: string | null;
  last_sync_at: string | null;
  created_at: string;
  revoked_at: string | null;
}

interface MyCredentialItem {
  guest_pass_id: string;
  guest_name: string;
  valid_from: string;
  valid_until: string;
  credential: CredentialLite;
}

function statusTone(s: CredentialLite['status']): 'success' | 'warning' | 'danger' {
  if (s === 'active') return 'success';
  if (s === 'pending') return 'warning';
  return 'danger';
}

function syncTone(s: CredentialLite['sync_status']): 'success' | 'warning' | 'danger' | 'neutral' {
  if (s === 'synced') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'syncing' || s === 'pending') return 'warning';
  return 'neutral';
}

export function MyCredentialsPage() {
  const [items, setItems] = useState<MyCredentialItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isCompact = useIsCompact();

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ data: MyCredentialItem[] }>('/api/v1/me/credentials')
      .then((res) => {
        if (!cancelled) setItems(res.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load credentials');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <span className="rounded-full bg-primary-container p-2 text-on-primary">
          <Icon name="badge" />
        </span>
        <div>
          <h1 className="font-headline-sm text-headline-sm text-on-surface">
            Your guests' keys
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            The temporary keys and PINs for guests you've sponsored. Cancel a
            guest pass to turn off its key —{' '}
            <Link to="/me/home" className="text-primary hover:underline">
              manage guest passes on your home page
            </Link>
            .
          </p>
        </div>
      </header>

      {error && (
        <Card>
          <CardBody>
            <p className="font-body-md text-body-md text-error" role="alert">
              {error}
            </p>
          </CardBody>
        </Card>
      )}

      {!items && !error && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-3 text-on-surface-variant"
        >
          <Spinner />
          <span>Loading…</span>
        </div>
      )}

      {items !== null && items.length === 0 && !error && (
        <Card>
          <CardBody>
            <p className="font-body-md text-body-md text-on-surface-variant">
              No keys are active for your guests right now. When you sponsor a
              guest and they're handed a card or PIN, it shows up here until the
              pass ends.
            </p>
          </CardBody>
        </Card>
      )}

      {items && items.length > 0 && isCompact && (
        <ul className="space-y-3">
            {items.map((it) => (
              <li
                key={`${it.guest_pass_id}-${it.credential.id}`}
                className="rounded-lg border border-outline-variant bg-surface-container-lowest p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="font-title-sm text-title-sm text-on-surface">
                    {it.guest_name}
                  </span>
                  <Badge tone={statusTone(it.credential.status)}>
                    {credentialStatusLabel(it.credential.status)}
                  </Badge>
                </div>
                {it.credential.label && (
                  <div className="mt-0.5 font-body-md text-body-md text-on-surface-variant">
                    {it.credential.label}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Badge tone="info">
                    {credentialTypeLabel(it.credential.type, {
                      unifiCredentialId: it.credential.unifi_credential_id,
                      surface: 'resident_guest',
                    })}
                  </Badge>
                  {it.credential.sync_status !== 'synced' ? (
                    <Badge tone={syncTone(it.credential.sync_status)}>
                      {syncStatusLabel(it.credential.sync_status)}
                    </Badge>
                  ) : null}
                </div>
                <div className="mt-2 font-body-md text-body-md text-on-surface-variant">
                  Valid until {new Date(it.valid_until).toLocaleString()}
                </div>
                {it.credential.last_sync_error && (
                  <div className="mt-1 font-body-sm text-body-sm text-error">
                    {it.credential.last_sync_error}
                  </div>
                )}
              </li>
            ))}
        </ul>
      )}

      {items && items.length > 0 && !isCompact && (
        <div className="overflow-x-auto rounded-lg border border-outline-variant">
            <table className="w-full text-left font-body-md text-body-md">
            <thead className="bg-surface-container-low text-label-md font-label-md uppercase text-on-surface-variant">
              <tr>
                <th className="px-4 py-3">Guest</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Setup</th>
                <th className="px-4 py-3">Valid until</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {items.map((it) => (
                <tr key={`${it.guest_pass_id}-${it.credential.id}`} className="bg-surface">
                  <td className="px-4 py-3">
                    <span className="font-title-sm text-title-sm text-on-surface">
                      {it.guest_name}
                    </span>
                    {it.credential.label && (
                      <div className="text-on-surface-variant text-sm">
                        {it.credential.label}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone="info">
                      {credentialTypeLabel(it.credential.type, {
                        unifiCredentialId: it.credential.unifi_credential_id,
                        surface: 'resident_guest',
                      })}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={statusTone(it.credential.status)}>
                      {credentialStatusLabel(it.credential.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={syncTone(it.credential.sync_status)}>
                      {syncStatusLabel(it.credential.sync_status)}
                    </Badge>
                    {it.credential.last_sync_error && (
                      <div className="text-error text-sm mt-1">
                        {it.credential.last_sync_error}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-on-surface-variant">
                    {new Date(it.valid_until).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
